import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { IStringMap } from 'app/interfaces/IStringMap';
import { getValue } from 'app/extensibility/utils/property-bag.util';
import { Constants } from 'app/shared/shared-constants';
import {
    akvToModel,
    akvToResource,
    parameterizableSecretBaseToModel,
    parameterizablePasswordOrAKVtoSecretBase
} from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { AzureTableStorageLinkedService } from 'app/model/linkedservice/azure-table-storage.linkedservice';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { AdapterContext, azureStorageAuthTypes, ReferenceAdapter, Credential } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureTableStorageLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: AzureTableStorageLinkedService) => {
                    if (ls && !ls.fullyByJsonEditor && ls.authenticationMethod === azureStorageAuthTypes.AccountKeyAuthentication && !ls.enableAccountAKV) {
                        if (ls.accountKey instanceof AzureKeyVault) {
                            return '*';
                        }
                    }
                    return 'secret';
                },
                accountKey: 'secret',
                sasUri: 'secret',
                sasToken: 'secret',
                serviceEndpoint:  '*',
                credential: '*',
                encryptedCredential: '*'
            },
            parameters: '*'
        }
    }
})
export class AzureTableStorageLinkedServiceAdapter extends LinkedServiceAdapter<AzureTableStorageLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => AzureTableStorageLinkedService {
        return AzureTableStorageLinkedService;
    }

    protected model(): IModel {
        return AzureTableStorageLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: AzureTableStorageLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.AzureTableStorageLinkedService;

        properties.type = Metamodel.modelFor(AzureTableStorageLinkedService).name;

        if (linkedService.authenticationMethod === azureStorageAuthTypes.SasUriAuthentication) {
            if (linkedService.enableSasUriAKV && linkedService.referedSasUriAKV instanceof AzureKeyVault) {
                properties.typeProperties = {
                    sasUri: akvToResource(linkedService.referedSasUriAKV, this.context)
                };
            } else {
                let sasUri = this.valueToResource(linkedService, AzureTableStorageLinkedService.SAS_URI);
                if (linkedService.sasToken && isString(linkedService.sasToken)) {
                    let sasToken = linkedService.sasToken as string;
                    if (sasUri.endsWith('?')) {
                        sasUri = sasUri.substr(0, sasUri.length - 1);
                    }
                    if (sasToken.startsWith('?')) {
                        sasToken = sasToken.substr(1);
                    }
                    properties.typeProperties = {
                        sasUri: sasUri + '?' + sasToken
                    };
                } else {
                    properties.typeProperties = {
                        sasUri,
                        sasToken: parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureTableStorageLinkedService.SAS_TOKEN, this.context)
                    };
                }
            }
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.AccountKeyAuthentication) {
            if (!linkedService.enableAccountAKV) {
                const propertiesBag: IStringMap<string | Model.SecretBase | undefined> = {};
                propertiesBag['DefaultEndpointsProtocol'] = 'https';
                propertiesBag['AccountName'] = this.valueToResource(linkedService, AzureTableStorageLinkedService.ACCOUNT_NAME);
                if (linkedService.primaryEndpoint) {
                    // can't check `isPartitionedDns` property as azure subscription doesn't have this checkbox
                    const primaryEndpoint = this.valueToResource(linkedService, AzureTableStorageLinkedService.PRIMARY_ENDPOINT);
                    if (primaryEndpoint) {
                        propertiesBag['TableEndpoint'] = primaryEndpoint;
                        // if user changed existing LS from old to new, so endpointSuffix should be removed
                        // as they should be mutually exclusive
                        delete linkedService.endpointSuffix;
                    }
                } else {
                    const endpointSuffix = this.valueToResource(linkedService, AzureTableStorageLinkedService.ENDPOINT_SUFFIX);
                    if (endpointSuffix) {
                        propertiesBag['EndpointSuffix'] = endpointSuffix;
                        // if user changed existing LS from new to old, so primaryEndpoint should be removed
                        // as they should be mutually exclusive
                        delete linkedService.primaryEndpoint;
                    }
                }
                if (linkedService.accountKey && !(linkedService.accountKey instanceof AzureKeyVault)) { // string
                    propertiesBag['AccountKey'] = linkedService.accountKey;
                } else if (!linkedService.accountKey) { // parameterized
                    propertiesBag['AccountKey'] = parameterizablePasswordOrAKVtoSecretBase(linkedService, AzureTableStorageLinkedService.ACCOUNT_KEY, this.context);
                }
    
                if (linkedService.additionalFields) {
                    linkedService.additionalFields.forEach((pair) => {
                        if (pair.value != null) {
                            propertiesBag[pair.key] = pair.value;
                        }
                    });
                }
                let connectionString = '';
                Object.keys(propertiesBag).forEach((key) => {
                    const value = getValue(propertiesBag, key);
                    if (value != null) {
                        connectionString += `${key}=${value};`;
                    }
                });
                properties.typeProperties = {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    connectionString: linkedService.accountName === Constants.maskedCredential
                        && linkedService.accountKey === Constants.maskedCredential ?
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                        ({
                            type: 'SecureString',
                            value: Constants.maskedCredential
                        } as Model.SecureString) : this._connectionStringToResource(connectionString),
                    // 3 cases for accountKey: 1) AKV --> akvToResource, 2) parameterized --> stored expression, 3) string --> undefined
                    accountKey: linkedService.accountKey instanceof AzureKeyVault ? akvToResource(linkedService.accountKey, this.context) :
                        !linkedService.accountKey ? propertiesBag['AccountKey'] : undefined
                };
            } else {
                properties.typeProperties = {
                    connectionString: akvToResource(linkedService.referedAccountAKV, this.context)
                };
            }
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.MSIAuthentication) {
            properties.typeProperties = {
                serviceEndpoint: this.valueToResource(linkedService, AzureTableStorageLinkedService.SERVICE_ENDPOINT),
            };
        } else if (linkedService.authenticationMethod === azureStorageAuthTypes.UserAssignedManagedIdentity) {
            properties.typeProperties = {
                serviceEndpoint: this.valueToResource(linkedService, AzureTableStorageLinkedService.SERVICE_ENDPOINT),
                credential: linkedService.credential
                    ? (ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference)
                    : undefined
            };
        }
        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: AzureTableStorageLinkedService): Promise<AzureTableStorageLinkedService> {
        const properties = linkedServiceResource.properties as Model.AzureTableStorageLinkedService;

        linkedService.accountName = undefined;
        linkedService.accountKey = undefined;
        linkedService.referedAccountAKV = undefined;
        linkedService.sasToken = undefined;
        linkedService.sasUri = undefined;
        linkedService.serviceEndpoint = undefined;
        linkedService.credential = undefined;
        linkedService.referedSasUriAKV = undefined;
        linkedService.authenticationMethod = azureStorageAuthTypes.AccountKeyAuthentication;
        linkedService.additionalFields = [];

        if (properties.typeProperties.sasUri) {
            linkedService.authenticationMethod = azureStorageAuthTypes.SasUriAuthentication;
            if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.sasUri)) {
                linkedService.enableSasUriAKV = false;
                linkedService.sasUri = Constants.maskedCredential;
                parameterizableSecretBaseToModel(linkedService, AzureTableStorageLinkedService.SAS_TOKEN, properties.typeProperties.sasToken, this.context);
            } else if ((properties.typeProperties.sasUri as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.enableSasUriAKV = true;
                linkedService.referedSasUriAKV = akvToModel(properties.typeProperties.sasUri as Model.AzureKeyVaultSecretReference, this.context);
            } else { // string & null (parameterized)
                linkedService.enableSasUriAKV = false;
                ValueAdapter.toModel(linkedService, AzureTableStorageLinkedService.SAS_URI, properties.typeProperties.sasUri);
                parameterizableSecretBaseToModel(linkedService, AzureTableStorageLinkedService.SAS_TOKEN, properties.typeProperties.sasToken, this.context);
            }
        } else if (properties.typeProperties.serviceEndpoint) {
            ValueAdapter.toModel(
                linkedService,
                AzureTableStorageLinkedService.SERVICE_ENDPOINT,
                properties.typeProperties.serviceEndpoint
            );
            if (properties.typeProperties.credential) {
                linkedService.credential = ReferenceAdapter.toProxyModel(
                    Metamodel.modelFor(Credential),
                    properties.typeProperties.credential, this.context
                );
                linkedService.authenticationMethod = azureStorageAuthTypes.UserAssignedManagedIdentity;
            } else {
                linkedService.authenticationMethod = azureStorageAuthTypes.MSIAuthentication;
            }   
        } else if (isString(properties.typeProperties.connectionString)) {
            const keyValPairs = (properties.typeProperties.connectionString as string).split(';');
            keyValPairs.forEach((item) => {
                // assume key doesn't contain '='
                const sepIndex = item.indexOf('=');
                if (sepIndex >= 0) {
                    const key = item.substr(0, sepIndex);
                    const val = item.substr(sepIndex + 1);

                    switch (key) {
                        case 'AccountName':
                            ValueAdapter.toModel(linkedService, AzureTableStorageLinkedService.ACCOUNT_NAME, val);
                            break;
                        case 'EndpointSuffix':
                            ValueAdapter.toModel(linkedService, AzureTableStorageLinkedService.ENDPOINT_SUFFIX, val);
                            break;
                        case 'AccountKey':
                            parameterizableSecretBaseToModel(linkedService, AzureTableStorageLinkedService.ACCOUNT_KEY, properties.typeProperties.accountKey, this.context);
                            break;
                        case 'TableEndpoint':
                                ValueAdapter.toModel(linkedService, AzureTableStorageLinkedService.PRIMARY_ENDPOINT, val);
                            break;
                        case 'DefaultEndpointsProtocol':
                            if (val !== 'https') {
                                linkedService.additionalFields.push({ key, value: val });
                            }
                            break;
                        default:
                            linkedService.additionalFields.push({ key, value: val });
                    }
                }
            });
                linkedService.isPartitionedDns = !!linkedService.primaryEndpoint;
            linkedService.accountKey = Constants.maskedCredential;
            if (properties.typeProperties.accountKey) {
                parameterizableSecretBaseToModel(linkedService, AzureTableStorageLinkedService.ACCOUNT_KEY, properties.typeProperties.accountKey, this.context);
            }
        } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
            linkedService.accountName = Constants.maskedCredential;
            linkedService.accountKey = Constants.maskedCredential;
            if (properties.typeProperties.accountKey) {
                parameterizableSecretBaseToModel(linkedService, AzureTableStorageLinkedService.ACCOUNT_KEY, properties.typeProperties.accountKey, this.context);
            }
        } else if (properties.typeProperties.connectionString && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
            linkedService.referedAccountAKV = akvToModel(properties.typeProperties.connectionString as Model.SecretBase, this.context);
            linkedService.enableAccountAKV = true;
        }

        return linkedService;
    }
}
